import { ArrowLeftOutlined, DeleteOutlined } from '@ant-design/icons';
import { gql, useMutation } from '@apollo/client';
import {
  Button,
  Divider,
  Form,
  Input,
  notification,
  Space,
  Typography,
} from 'antd';
import SelectTenant from '../../tenants/SelectTenant';

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import CSPage from '../../../components/CSPage';
import CSPageHeader from '../../../components/CSPageHeader';
import {
  createApiKeyAction,
  updateApiKeyAction,
} from '../../../redux-store/api-keys-store';
import useIsCreating from '../../../shared/use-is-creating';
import useOne from '../../../shared/use-one';
import SelectRole from '../../roles/SelectRole';
import {
  apiKeyQuery,
  createApiKeyMutation,
  updateApiKeyMutation,
} from '../constants';
import DeleteApiKeyModal from '../DeleteApiKeyModal';
import ShowPrivateKeyModal from '../ShowPrivateKeyModal';

const { Text } = Typography;

function EditApiKey() {
  const history = useHistory();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [updateApiKey] = useMutation(updateApiKeyMutation);
  const [createApiKey] = useMutation(createApiKeyMutation);
  const dispatch = useDispatch();

  const { _id } = useParams();
  const queryId = useMemo(() => {
    return Math.round(Math.random() * 10000);
  }, []);

  const fetchResult = useOne(
    apiKeyQuery,
    (data) => data && data.apiKey,
    updateApiKeyAction,
    'apiKeys',
    _id,
    queryId,
  );

  const [form] = Form.useForm();
  const labelInput = useRef(null);
  const [apiKey, setApiKey] = useState();

  const onlyOnce = useRef(false);
  useEffect(() => {
    if (!fetchResult.loading && !onlyOnce.current && fetchResult.data) {
      setApiKey(fetchResult.data);
      onlyOnce.current = true;
    }
  }, [fetchResult, loading]);

  const [createdkey, setCreatedKey] = useState(null);

  useEffect(() => {
    if (apiKey) {
      const { ...otherFields } = apiKey;
      form.setFieldsValue(otherFields);
    }
  }, [form, apiKey]);

  useLayoutEffect(() => {
    if (labelInput.current) {
      labelInput.current.focus();
    }
  }, []);

  const handleBack = useCallback(() => history.goBack(), [history]);

  const onSubmit = useCallback(
    async (_values) => {
      setLoading(true);
      setError(null);
      try {
        const { tenantId, ...values } = _values;
        if (apiKey) {
          const result = await updateApiKey({
            variables: {
              apiKey: {
                _id,
                tenantId: tenantId || null,
                ...values,
              },
            },
          });
          dispatch(updateApiKeyAction(result.data.updateApiKey.apiKey));
          notification.success({
            message: 'Saved',
            description: 'API Key updated successfully',
          });
          history.push('/api-keys');
        } else {
          const result = await createApiKey({
            variables: {
              apiKey: {
                _id,
                tenantId: tenantId || null,
                ...values,
              },
            },
          });
          dispatch(createApiKeyAction(result.data.createApiKey.apiKey));
          setCreatedKey(result.data.createApiKey.apiKey);
        }
      } catch (err) {
        console.error(err);
        setError(err.message);
      }
      setLoading(false);
    },
    [apiKey, _id, history, createApiKey, updateApiKey, dispatch],
  );

  const onPrivateKeyModalClosed = useCallback(() => {
    notification.success({
      message: 'Saved',
      description: 'API Key created successfully',
    });
    history.push('/api-keys');
  }, [history]);

  const { title, showDelete } = useIsCreating(
    location,
    apiKey,
    loading,
    'Create an API Key',
    'Edit API Key',
  );

  return (
    <>
      <CSPage>
        <CSPageHeader
          title={title}
          backActions={[
            <Button
              key="back"
              type="text"
              onClick={handleBack}
              icon={<ArrowLeftOutlined />}
            >
              Back to API Keys
            </Button>,
          ]}
        />
        <Form
          layout="vertical"
          onFinish={onSubmit}
          id="editApiKey"
          form={form}
          style={{ maxWidth: 500 }}
        >
          <Form.Item
            label="Label"
            name="label"
            rules={[{ required: true, message: 'Please enter a label' }]}
          >
            <Input ref={labelInput} disabled={loading} />
          </Form.Item>
          <Form.Item
            label="Description"
            name="desc"
            rules={[{ required: true, message: 'Please enter a description' }]}
          >
            <Input disabled={loading} />
          </Form.Item>
          <Form.Item
            label="Role"
            name="roleId"
            rules={[{ required: true, message: 'Please select a role' }]}
          >
            <SelectRole disabled={loading || fetchResult.loading} />
          </Form.Item>
          <Form.Item label="Tenant" name="tenantId" rules={[]}>
            <SelectTenant
              disabled={loading || fetchResult.loading}
              allowClear
            />
          </Form.Item>
          <Form.Item
            label="Issuer"
            name="issuer"
            rules={[{ required: true, message: 'Please enter an issuer' }]}
          >
            <Input disabled={loading} />
          </Form.Item>
          <Form.Item
            label="Audience"
            name="audience"
            rules={[{ required: true, message: 'Please enter an audience' }]}
          >
            <Input disabled={loading} />
          </Form.Item>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldsError }) => {
              const errorList = getFieldsError();
              let showError = false;
              errorList.forEach((errors) => {
                if (errors.errors.length) {
                  showError = true;
                }
              });
              return (
                showError && (
                  <Text type="danger" style={{ marginTop: 16 }}>
                    Please correct the errors above
                  </Text>
                )
              );
            }}
          </Form.Item>
          <div style={{ height: 16 }} />
          <Form.Item>
            <Space
              style={{
                width: '100%',
                justifyContent: 'space-between',
              }}
            >
              <Button
                key="cancel"
                onClick={handleBack}
                htmlType="button"
                type="text"
                size="small"
                disabled={loading}
                icon={<ArrowLeftOutlined />}
                style={{ marginLeft: -7 }}
              >
                Cancel
              </Button>
              <Button
                key="send"
                type="primary"
                loading={loading}
                htmlType="submit"
              >
                Save API Key
              </Button>
            </Space>
          </Form.Item>
          {error && (
            <div
              className="server-error ant-form-item-has-error"
              style={{ marginTop: 16 }}
            >
              <div className="ant-form-item-explain">{error}</div>
            </div>
          )}
          {showDelete && (
            <>
              <Divider />
              <div className="delete-box">
                <Space align="center">
                  <Button
                    onClick={() => setDeleting(apiKey)}
                    icon={<DeleteOutlined />}
                    danger
                    ghost
                  >
                    Delete API Key
                  </Button>
                </Space>
              </div>
            </>
          )}
        </Form>
        <div style={{ minHeight: 300 }} />
        <DeleteApiKeyModal
          onFinish={() => {
            setApiKey(null);
            history.goBack();
          }}
          onCancel={() => setApiKey(null)}
          apiKey={deleting}
        />
        <ShowPrivateKeyModal
          apiKey={createdkey}
          onYes={onPrivateKeyModalClosed}
        />
      </CSPage>
      <style jsx>{`
        .delete-box {
          display: flex;
          justify-content: center;
        }
      `}</style>
    </>
  );
}

export default EditApiKey;
